import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Minimize2, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { saveDraft, removeDraft, newDraftId, type ContactDraft } from "@/lib/draftModals"
import { AddressFields } from "./AddressFields"
import { KONTAKT_ARTEN } from "@/lib/kontaktArten"

interface HeroOptions {
  project_types?: { id: number; name: string; steps?: { id: number; name: string }[] }[]
  measures?: { id: number; name: string }[]
  partners?: { id: number; name: string }[]
  monteure?: { id: number; name: string }[]
}

/**
 * HERO-Kontakt (+ optional Projekt) anlegen — 1:1 wie altes sg-modal.
 * Felder: Vorname/Nachname/Telefon/Email/Adresse
 * + wenn Projekt: Projekttyp/Gewerk/Mitarbeiter/Projekt-Status/Projektname
 *   + Abweichender Ansprechpartner (Checkbox) + Projekt-Beschreibung
 */
export function CreateContactModal({
  open, onClose, mode, itemId, onSuccess,
  tenantSlug,
  prefillPhone, prefillFirst, prefillLast, prefillEmail,
  prefillStreet, prefillZipcode, prefillCity,
  prefillDescription, prefillSalutation, prefillProjectName,
  prefillMeasureId, prefillProjectTypeId, prefillPartnerId, prefillStepId,
  initialWithProject, projectOnly,
  initialUseExistingContact,
  draftId,
  draftPrefill,
}: {
  open: boolean
  onClose: () => void
  mode: "sipgate" | "meiti" | "email" | "calendar" | "portal"
  /** itemId ist im calendar-mode 0 (kein E-Mail/Call gebunden) */
  itemId: number
  /** Pflicht bei mode="calendar" — der Tenant fuer Hero-Options + Project-Create */
  tenantSlug?: string
  onSuccess: (result?: { project_id?: number; contact_id?: number }) => void
  prefillPhone?: string
  prefillFirst?: string
  prefillLast?: string
  prefillEmail?: string
  prefillStreet?: string
  prefillZipcode?: string
  prefillCity?: string
  prefillDescription?: string
  prefillSalutation?: string
  prefillProjectName?: string
  /** Vorbelegte HERO-Projekt-Felder (z.B. aus Review-Plan) */
  prefillMeasureId?: number | null
  prefillProjectTypeId?: number | null
  prefillPartnerId?: number | null
  prefillStepId?: number | null
  initialWithProject?: boolean
  /** Wenn true: Kontakt existiert bereits, nur Projekt-Felder zeigen, nutzt /hero-project Endpoint */
  projectOnly?: boolean
  /** Calendar-Mode only: Wenn true → Modal startet im "Bestehenden Kontakt suchen"-Pfad
   * statt neuen Kontakt anzulegen. Body wird dann mit contact_id (statt new_contact) gesendet. */
  initialUseExistingContact?: boolean
  /** Optional: vorausgefuellte Draft-ID (fuer Restore aus MinimizedDraftsBar) */
  draftId?: string
  /** Optional: vorausgefuellte Felder aus Draft */
  draftPrefill?: Partial<ContactDraft>
}) {
  const qc = useQueryClient()
  // portal-Mode (Kontakte-Seite): startet als „nur Kontakt", per Toggle
  // optional „+ Projekt/Objekt". Andere Modi behalten ihr festes Verhalten.
  const [portalWithProject, setPortalWithProject] = useState(initialWithProject ?? false)
  const withProject = (mode === "calendar") ? true
    : (projectOnly ? true
    : (mode === "portal" ? portalWithProject
    : (initialWithProject ?? false)))
  const [salutation, setSalutation] = useState(draftPrefill?.salutation || prefillSalutation || "Herr")
  const [first, setFirst] = useState(draftPrefill?.first_name || prefillFirst || "")
  const [last, setLast] = useState(draftPrefill?.last_name || prefillLast || "")
  // Firma: bis zum 27.08.2026 gab es das Feld hier gar nicht — aus einer Mail
  // liess sich damit kein Firmenkontakt anlegen, der Firmenname ging verloren
  // (#49). HERO fuehrt ihn als `company_name` am Kontakt.
  const [firma, setFirma] = useState(draftPrefill?.company_name || "")
  // Art des Kontakts (#50): danach wurde beim Anlegen gar nicht gefragt.
  // „Kunde" als Vorgabe, weil das bei 1108 von 1351 Kontakten stimmt — sichtbar
  // vorbelegt, nicht stumm gesetzt.
  const [art, setArt] = useState(draftPrefill?.category || "customer")
  const [phone, setPhone] = useState(draftPrefill?.phone_mobile || prefillPhone || "")
  const [email, setEmail] = useState(draftPrefill?.email || prefillEmail || "")
  const [street, setStreet] = useState(draftPrefill?.street || prefillStreet || "")
  const [zipcode, setZip] = useState(draftPrefill?.zipcode || prefillZipcode || "")
  const [city, setCity] = useState(draftPrefill?.city || prefillCity || "")
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(draftId || null)

  // Calendar-only: Toggle "Bestehenden Kontakt verwenden" — verbirgt die
  // Kontakt-Eingabefelder und nutzt den /create-project body.contact_id-Pfad.
  // Bei Re-Open mit anderem initialUseExistingContact-Wert muss der State
  // nachgezogen werden — sonst klebt die alte Auswahl (typisch React-Setup).
  const [useExisting, setUseExisting] = useState(!!initialUseExistingContact && mode === "calendar")
  useEffect(() => {
    if (open) setUseExisting(!!initialUseExistingContact && mode === "calendar")
  }, [open, initialUseExistingContact, mode])
  const [existingContact, setExistingContact] = useState<null | {
    id: number; salutation: string; first_name: string; last_name: string;
    email?: string; phone_mobile?: string; street?: string; zipcode?: string; city?: string;
  }>(null)
  const [contactQuery, setContactQuery] = useState("")
  const [contactResults, setContactResults] = useState<typeof existingContact extends infer _T ? Array<NonNullable<typeof existingContact>> : never>([])
  const [contactSearching, setContactSearching] = useState(false)

  const searchContacts = async (q: string) => {
    if (q.length < 2 || !tenantSlug) { setContactResults([]); return }
    setContactSearching(true)
    try {
      const r = await fetch(
        `/api/v1/contacts?tenant=${encodeURIComponent(tenantSlug)}&q=${encodeURIComponent(q)}&limit=20`,
        { credentials: "include" },
      )
      const d = await r.json()
      const items = (d.items || []).map((i: Record<string, unknown>) => ({
        id: i.id as number,
        salutation: (i.salutation as string) || "",
        first_name: (i.first_name as string) || "",
        last_name: (i.last_name as string) || "",
        email: (i.email as string) || "",
        phone_mobile: (i.phone_mobile as string) || "",
        street: (i.street as string) || "",
        zipcode: (i.zipcode as string) || "",
        city: (i.city as string) || "",
      }))
      setContactResults(items)
    } catch {
      setContactResults([])
    } finally {
      setContactSearching(false)
    }
  }

  useEffect(() => { if (open && draftId) setCurrentDraftId(draftId) }, [open, draftId])

  // #132 (Marlene, 25.09.2026): „der hat mehrere Objektadressen, die müssen
  // vorgeschlagen werden". Einsatzorte aus den bisherigen Vorgaengen des
  // gewaehlten Kontakts — ein Klick fuellt „Abweichende Projektadresse".
  // Die Stammadresse steht ohnehin schon am Kontakt, sie kommt nicht dazu.
  type Objektadresse = { street: string; zipcode: string; city: string; vorgaenge: string[] }
  const [objektadressen, setObjektadressen] = useState<Objektadresse[]>([])
  useEffect(() => {
    setObjektadressen([])
    if (!open || !existingContact?.id || !tenantSlug) return
    let abgebrochen = false
    fetch(
      `/api/v1/contacts/${existingContact.id}/objektadressen?tenant=${encodeURIComponent(tenantSlug)}`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d: { items?: Objektadresse[] }) => {
        if (abgebrochen) return
        const norm = (x: string) => x.toLowerCase().replace(/straße|strasse|str\./g, "str").replace(/\s+/g, "")
        const stamm = norm(`${existingContact.street || ""}|${existingContact.zipcode || ""}`)
        setObjektadressen((d.items || []).filter((a) => norm(`${a.street}|${a.zipcode}`) !== stamm))
      })
      .catch(() => { /* Vorschlag ist Komfort — ohne ihn bleibt die Eingabe von Hand */ })
    return () => { abgebrochen = true }
  }, [open, existingContact?.id, existingContact?.street, existingContact?.zipcode, tenantSlug])
  // Projekt-spezifisch
  const [projectTypeId, setProjectTypeId] = useState("")
  const [measureId, setMeasureId] = useState("")
  const [partnerId, setPartnerId] = useState("")
  const [stepId, setStepId] = useState("")
  const [projectName, setProjectName] = useState("")
  const [separateProjectContact, setSeparateProjectContact] = useState(false)
  const [description, setDescription] = useState(prefillDescription || "")
  // fortytools-Objekt-Felder (nur wenn backend_provider='fortytools')
  const [facilityName, setFacilityName] = useState("")
  const [bunchofkeys, setBunchofkeys] = useState("")
  const [facilityNotes, setFacilityNotes] = useState("")
  // Projektadresse (abweichend vom Kontakt) — collapse-by-default
  const [projectAddressOpen, setProjectAddressOpen] = useState(false)
  const [projStreet, setProjStreet] = useState("")
  const [projZipcode, setProjZipcode] = useState("")
  const [projCity, setProjCity] = useState("")
  // Abweichender Ansprechpartner (fuer ContactProjectRequest.contact_person + ProjectOnlyRequest.contact_person)
  const [cpSalutation, setCpSalutation] = useState("Herr")
  const [cpFirst, setCpFirst] = useState("")
  const [cpLast, setCpLast] = useState("")
  const [cpPhone, setCpPhone] = useState("")
  const [cpEmail, setCpEmail] = useState("")
  const [cpRechnungsempfaenger, setCpRechnungsempfaenger] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // HERO-Options laden — fuer "calendar" wird der bestehende calendar/options-
  // Endpoint genutzt (liefert die gleichen Felder: measures/project_types/partners).
  // Bugfix 11.05.2026: Sipgate hatte `/admin/sipgate/{id}/hero-options` —
  // existiert nicht. Jetzt v1: `/api/v1/sipgate/{id}/hero-options`.
  const heroOptionsUrl =
    mode === "sipgate" ? `/api/v1/sipgate/${itemId}/hero-options` :
    mode === "meiti" ? `/api/v1/calls/${itemId}/hero-options` :
    mode === "email" ? `/api/v1/emails/${itemId}/hero-options` :
    (mode === "calendar" || mode === "portal") && tenantSlug ? `/api/v1/calendar/options?tenant=${encodeURIComponent(tenantSlug)}` :
    null
  const options = useQuery<HeroOptions>({
    queryKey: ["hero-options", mode, itemId, tenantSlug || ""],
    queryFn: async () => {
      if (!heroOptionsUrl) return {}
      const res = await fetch(heroOptionsUrl, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && withProject,
  })

  // Backend-Provider des Tenants -> steuert, ob HERO- oder fortytools-Objekt-
  // Felder gezeigt werden. Ohne tenantSlug (oder Fehler) faellt es auf HERO
  // zurueck -> Verhalten unveraendert.
  const tenantInfo = useQuery<{ backend_provider?: string } | null>({
    queryKey: ["tenant-provider", tenantSlug || ""],
    queryFn: async () => {
      if (!tenantSlug) return null
      const res = await fetch(`/api/v1/tenants/${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!res.ok) return null
      return res.json()
    },
    enabled: open && !!tenantSlug,
  })
  const isForty = (tenantInfo.data?.backend_provider || "hero") === "fortytools"

  // Reset bei Open — mit Prefill-Werten aus Props (Meiti/Sipgate-Transkript/Review-Plan)
  useEffect(() => {
    if (open) {
      setSalutation(prefillSalutation || "Herr")
      setFirst(prefillFirst || "")
      setLast(prefillLast || "")
      // Aus einem wiederhergestellten Entwurf kommt die Firma zurueck, sonst leer.
      setFirma(draftPrefill?.company_name || "")
      setArt(draftPrefill?.category || "customer")
      setPhone(prefillPhone || "")
      setEmail(prefillEmail || "")
      setStreet(prefillStreet || "")
      setZip(prefillZipcode || "")
      setCity(prefillCity || "")
      setProjectTypeId(prefillProjectTypeId ? String(prefillProjectTypeId) : "")
      setMeasureId(prefillMeasureId ? String(prefillMeasureId) : "")
      setPartnerId(prefillPartnerId ? String(prefillPartnerId) : "")
      setStepId(prefillStepId ? String(prefillStepId) : "")
      setPortalWithProject(initialWithProject ?? false)
      setProjectName(prefillProjectName || ""); setSeparateProjectContact(false)
      setDescription(prefillDescription || "")
      setFacilityName(prefillProjectName || ""); setBunchofkeys(""); setFacilityNotes(prefillDescription || "")
      setProjectAddressOpen(false); setProjStreet(""); setProjZipcode(""); setProjCity("")
      setCpSalutation("Herr"); setCpFirst(""); setCpLast(""); setCpPhone(""); setCpEmail("")
      setErr(null)
    }
  }, [open, prefillPhone, prefillFirst, prefillLast, prefillEmail, prefillStreet, prefillZipcode, prefillCity, prefillDescription, prefillSalutation, prefillProjectName, prefillMeasureId, prefillProjectTypeId, prefillPartnerId, prefillStepId, initialWithProject])

  // Steps-Dropdown passt sich an Projekttyp an
  const selectedType = options.data?.project_types?.find((p) => String(p.id) === projectTypeId)
  const availableSteps = selectedType?.steps || []

  const base =
    mode === "sipgate" ? `/api/v1/sipgate/${itemId}` :
    mode === "meiti" ? `/api/v1/calls/${itemId}` :
    mode === "email" ? `/api/v1/emails/${itemId}` :
    `/api/v1/calendar`  // calendar-mode: nicht item-bound, tenantSlug im body

  const create = useMutation({
    mutationFn: async () => {
      // portal-Mode, nur Kontakt (kein Projekt): eigener, tenant-basierter
      // Endpoint — legt Kontakt (HERO) bzw. Kunden (fortytools) an.
      if (mode === "portal" && !withProject) {
        const res = await fetch(`/api/v1/contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            tenant: tenantSlug,
            salutation, first_name: first, last_name: last,
            company_name: firma, category: art,
            email, phone, street, zipcode, city,
          }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
        return json
      }

      // calendar-mode + portal-mit-Projekt teilen sich den create-project-Endpoint
      if (mode === "calendar" || mode === "portal") {
        // Default-Projektname: bei bestehendem Kontakt aus dessen Namen, sonst aus Eingabe.
        const defaultName = useExisting && existingContact
          ? `${existingContact.first_name} ${existingContact.last_name}`.trim() || "Neues Projekt"
          : `${first} ${last}`.trim() || "Neues Projekt"
        const body: Record<string, unknown> = {
          tenant: tenantSlug,
          name: projectName || defaultName,
          description,
        }
        // Kontakt: entweder bestehenden via contact_id oder neu anlegen via new_contact.
        if (useExisting && existingContact) {
          body.contact_id = existingContact.id
        } else {
          body.new_contact = {
            salutation,
            first_name: first || "Unbekannt",
            last_name: last,
            company_name: firma, category: art,
            phone, email, street, zip: zipcode, city,
          }
        }
        if (measureId) body.measure_id = parseInt(measureId)
        if (projectTypeId) body.type_id = parseInt(projectTypeId)
        if (stepId) body.step_id = parseInt(stepId)
        if (partnerId) body.partner_id = parseInt(partnerId)
        if (projectAddressOpen) {
          body.project_address = { street: projStreet, zip: projZipcode, city: projCity }
        }
        // fortytools-Objekt-Felder (HERO ignoriert sie serverseitig)
        if (isForty) {
          if (facilityName || projectName) body.facility_name = facilityName || projectName
          if (bunchofkeys) body.bunchofkeys = bunchofkeys
          if (facilityNotes) body.facility_notes = facilityNotes
        }
        if (cpFirst.trim() || cpLast.trim()) {
          body.contact_person = {
            salutation: cpSalutation,
            first_name: cpFirst.trim(),
            last_name: cpLast.trim(),
            phone: cpPhone.trim(),
            email: cpEmail.trim(),
            // Nur senden, wenn gesetzt: ohne Haken bleibt der Body wie bisher.
            ...(cpRechnungsempfaenger ? { is_invoice_recipient: true } : {}),
          }
        }
        const res = await fetch(`${base}/create-project`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
        return json
      }

      const url = projectOnly
        ? `${base}/hero-project`
        : (withProject ? `${base}/hero-contact-project` : `${base}/hero-contact`)
      // Bei `projectOnly` haengt an der Mail nicht zwingend schon ein
      // HERO-Kontakt. Der Server legt ihn dann mit an (#73) — dafuer bekommt er
      // die Daten mit, die im Plan stehen. Hier bewusst OHNE das „Unbekannt",
      // das der Kontakt-Weg als Pflichtwert setzt: bleibt der Vorname leer,
      // soll serverseitig der Absender einspringen, nicht ein Platzhalter.
      // 🔴 Das Feld heisst im Rumpf `zip`, nicht `zipcode`: die Endpunkte
      // `/hero-contact` und `/hero-contact-project` lesen `body.get("zip")`
      // (hero_link.py:195 und :367). Mit `zipcode` kam die PLZ nie an — der
      // Kontakt wurde ohne sie angelegt, ohne Fehlermeldung. An anderer Stelle
      // in dieser Datei (create-project) stand `zip: zipcode` laengst richtig.
      const body: Record<string, unknown> = projectOnly
        ? { salutation, first_name: first, last_name: last, company_name: firma, category: art, phone, email, street, city, zip: zipcode }
        : { salutation, first_name: first || "Unbekannt", last_name: last, company_name: firma, category: art, phone, email, street, city, zip: zipcode }
      if (withProject) {
        if (projectTypeId) body.project_type_id = parseInt(projectTypeId)
        if (measureId) body.measure_id = parseInt(measureId)
        if (partnerId) body.partner_id = parseInt(partnerId)
        if (stepId) body.step_id = parseInt(stepId)
        if (projectName) body.project_name = projectName
        if (description) body.description = description
        // fortytools-Objekt-Felder (HERO ignoriert sie serverseitig)
        if (isForty) {
          if (facilityName || projectName) body.facility_name = facilityName || projectName
          if (bunchofkeys) body.bunchofkeys = bunchofkeys
          if (facilityNotes) body.facility_notes = facilityNotes
        }
        if (!projectOnly && separateProjectContact) body.separate_project_contact = true
        // Projektadresse (optional, abweichend vom Kontakt)
        if (projectAddressOpen) {
          if (projStreet) body.proj_street = projStreet
          if (projZipcode) body.proj_zipcode = projZipcode
          if (projCity) body.proj_city = projCity
        }
        // Abweichender Ansprechpartner (fuer Projekt, nicht Kontakt-Ebene)
        if (((projectOnly) || separateProjectContact) && (cpFirst.trim() || cpLast.trim())) {
          body.contact_person = {
            salutation: cpSalutation,
            first_name: cpFirst.trim(),
            last_name: cpLast.trim(),
            phone: cpPhone.trim(),
            email: cpEmail.trim(),
            // #16: auch aus der Mail — die Endpunkte lesen das Feld laengst
            ...(cpRechnungsempfaenger ? { is_invoice_recipient: true } : {}),
          }
        }
      }
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: (json: any) => {
      if (mode === "portal") {
        qc.invalidateQueries({ queryKey: ["contacts"] })
      } else if (mode === "email") {
        qc.invalidateQueries({ queryKey: ["email"] })
        qc.invalidateQueries({ queryKey: ["emails"] })
      } else if (mode === "calendar") {
        qc.invalidateQueries({ queryKey: ["calendar"] })
        qc.invalidateQueries({ queryKey: ["calendar-events"] })
      } else {
        qc.invalidateQueries({ queryKey: [mode === "sipgate" ? "sipgate-call" : "meiti-call"] })
        qc.invalidateQueries({ queryKey: [mode === "sipgate" ? "sipgate-calls" : "meiti-calls"] })
      }
      toast(mode === "calendar"
        ? "✓ Projekt angelegt"
        : projectOnly ? "✓ Projekt angelegt" : (withProject ? "✓ Kontakt + Projekt angelegt" : "✓ Kontakt angelegt"))
      if (currentDraftId) removeDraft(currentDraftId)
      onSuccess({ project_id: json?.project_id, contact_id: json?.contact_id })
      onClose()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Anlegen fehlgeschlagen: " + msg, "error")
    },
  })

  // Minimieren = Draft in Store schreiben + Modal schliessen.
  // Pill erscheint global (AppLayout → MinimizedDraftsBar).
  const handleMinimize = () => {
    if (mode === "calendar" || mode === "portal") return  // kein Draft-Persistence-Pfad
    const id = currentDraftId || newDraftId()
    const draft: ContactDraft = {
      kind: "contact",
      id,
      created_at: Date.now(),
      source: mode as "sipgate" | "meiti" | "email",
      sourceId: itemId,
      salutation, first_name: first, last_name: last,
      company_name: firma,
      category: art,
      phone_mobile: phone, email,
      street, zipcode, city,
      withProject: !!withProject, projectOnly: !!projectOnly,
      projectName, projectDescription: description,
    }
    saveDraft(draft)
    setCurrentDraftId(id)
    onClose()
  }

  if (!open) return null

  // fortytools spricht von Kunde/Objekt statt Kontakt/Projekt.
  const _tContact = isForty ? "Kunde" : "Kontakt"
  const _tProject = isForty ? "Objekt" : "Projekt"
  const title = projectOnly
    ? `${_tProject} anlegen`
    : (mode === "calendar" && useExisting)
    ? `${_tProject} für bestehenden ${_tContact}`
    : (withProject ? `${_tContact} + ${_tProject} anlegen` : `${_tContact} anlegen`)

  return createPortal(
    <div
      className="pp-overlay oben"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "24px 16px", overflowY: "auto",
        backdropFilter: "blur(4px)",
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={(ev) => {
          // stopPropagation: React-Synthetic-Events bubbeln durch den React-Tree
          // auch bei createPortal — sonst wuerde der Submit eines Parent-<form>
          // (z.B. CalendarCreateModal) ebenfalls feuern und einen Default-Termin
          // anlegen + das Parent-Modal schliessen.
          ev.preventDefault()
          ev.stopPropagation()
          setErr(null)
          create.mutate()
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, padding: 28,
          maxWidth: 520, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)",
          margin: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <div style={{ display: "flex", gap: 2 }}>
            {!(mode === "calendar" || mode === "portal") && (
            <button
              type="button"
              onClick={handleMinimize}
              title="Minimieren — als Entwurf speichern (bleibt bei Seitenwechsel erhalten)"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#64748b", padding: "4px 8px", borderRadius: 6,
                display: "inline-flex", alignItems: "center",
              }}
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            )}
            <button
              type="button"
              onClick={onClose}
              title="Schliessen — nicht gespeicherte Aenderungen gehen verloren"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#64748b", padding: "4px 8px", borderRadius: 6,
                display: "inline-flex", alignItems: "center",
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {err && (
          <div className="pp-alert pp-alert-err" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}

        {/* portal-Mode: optional gleich ein Projekt/Objekt mit anlegen. */}
        {mode === "portal" && (
          <label style={{
            display: "flex", alignItems: "center", gap: 8, fontSize: 13,
            marginBottom: 16, cursor: "pointer", padding: "8px 10px",
            background: "var(--secondary-bg)", borderRadius: 8,
          }}>
            <input
              type="checkbox"
              checked={portalWithProject}
              onChange={(e) => setPortalWithProject(e.target.checked)}
            />
            {isForty ? "Auch ein Objekt anlegen" : "Auch ein Projekt anlegen"}
          </label>
        )}

        {/* Calendar-Mode: Umschalter zwischen "Neuer Kontakt" und "Bestehenden Kontakt suchen". */}
        {mode === "calendar" && !projectOnly && (
          <div style={{ marginBottom: 16, display: "flex", gap: 6, padding: 4, background: "#f1f5f9", borderRadius: 8 }}>
            <button
              type="button"
              onClick={() => { setUseExisting(false); setExistingContact(null); setContactQuery(""); setContactResults([]) }}
              style={{
                flex: 1, padding: "6px 10px", borderRadius: 6, fontSize: 12.5,
                fontWeight: useExisting ? 500 : 600, cursor: "pointer", border: "none",
                background: useExisting ? "transparent" : "#fff",
                color: useExisting ? "#64748b" : "#1a1a2e",
                boxShadow: useExisting ? "none" : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              Neuer Kontakt
            </button>
            <button
              type="button"
              onClick={() => setUseExisting(true)}
              style={{
                flex: 1, padding: "6px 10px", borderRadius: 6, fontSize: 12.5,
                fontWeight: useExisting ? 600 : 500, cursor: "pointer", border: "none",
                background: useExisting ? "#fff" : "transparent",
                color: useExisting ? "#1a1a2e" : "#64748b",
                boxShadow: useExisting ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
              }}
            >
              Bestehender Kontakt
            </button>
          </div>
        )}

        {/* Bestehenden Kontakt suchen (nur wenn useExisting aktiv). */}
        {mode === "calendar" && useExisting && !projectOnly && (
          <div style={{ marginBottom: 16 }}>
            {existingContact ? (
              <div style={{
                padding: "10px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0",
                borderRadius: 8, display: "flex", alignItems: "center", gap: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>
                    {existingContact.salutation} {existingContact.first_name} {existingContact.last_name}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569" }}>
                    Kontakt #{existingContact.id}
                    {existingContact.email && ` · ${existingContact.email}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setExistingContact(null); setContactQuery("") }}
                  style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b", padding: 4 }}
                  title="Anderen Kontakt waehlen"
                >×</button>
              </div>
            ) : (
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "#64748b", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Kontakt suchen *
                </label>
                <input
                  value={contactQuery}
                  onChange={(e) => { setContactQuery(e.target.value); searchContacts(e.target.value) }}
                  placeholder="Name, Telefon oder E-Mail (min. 2 Zeichen)"
                  autoFocus
                  style={{ width: "100%" }}
                />
                {contactSearching && <div style={{ fontSize: 12, color: "#64748b", padding: "4px 0" }}>Suche...</div>}
                {contactResults.length > 0 && (
                  <div style={{
                    marginTop: 4, maxHeight: 200, overflowY: "auto",
                    border: "1px solid #e5e7eb", borderRadius: 6,
                  }}>
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setExistingContact(c); setContactResults([]); setContactQuery("") }}
                        style={{
                          width: "100%", textAlign: "left", padding: "8px 12px",
                          borderBottom: "1px solid #f1f5f9", background: "none", border: "none",
                          cursor: "pointer", fontSize: 13,
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>
                          {c.salutation && <span style={{ color: "#64748b", marginRight: 4 }}>{c.salutation}</span>}
                          {c.first_name} {c.last_name}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {[c.email, c.phone_mobile].filter(Boolean).join(" · ") || `ID ${c.id}`}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {contactQuery.length >= 2 && !contactSearching && contactResults.length === 0 && (
                  <div style={{ fontSize: 12, color: "#64748b", padding: "6px 0" }}>
                    Keine Kontakte gefunden. Wechsle zu "Neuer Kontakt", um einen anzulegen.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!projectOnly && !(mode === "calendar" && useExisting) && (
          <>
            {/* Art des Kontakts | Anrede */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <F label="Art des Kontakts *">
                <select value={art} onChange={(e) => setArt(e.target.value)} required>
                  {KONTAKT_ARTEN.map((a2) => (
                    <option key={a2.wert} value={a2.wert}>{a2.label}</option>
                  ))}
                </select>
              </F>
              <F label="Anrede *">
                <select value={salutation} onChange={(e) => setSalutation(e.target.value)} required>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </select>
              </F>
            </div>

            {/* Firma (full width) — bei einem reinen Firmenkontakt der einzige Name */}
            <F label={<>Firma <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></>} style={{ marginBottom: 12 }}>
              <input
                value={firma}
                onChange={(e) => setFirma(e.target.value)}
                placeholder="z. B. Hoffmann & Co. GmbH"
              />
            </F>

            {/* Vorname | Nachname */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <F label="Vorname"><input value={first} onChange={(e) => setFirst(e.target.value)} /></F>
              {/* Nachname ist Pflicht — ausser es ist ein reiner Firmenkontakt.
                  Sonst liesse sich „Rhion Versicherung AG" gar nicht anlegen. */}
              <F label={firma.trim() ? "Nachname" : "Nachname *"}>
                <input value={last} onChange={(e) => setLast(e.target.value)} required={!firma.trim()} />
              </F>
            </div>

            {/* Telefon | E-Mail */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <F label="Telefon"><input value={phone} onChange={(e) => setPhone(e.target.value)} /></F>
              <F label="E-Mail"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></F>
            </div>

            {/* Strasse mit Offline-Vorschlaegen, darunter PLZ | Stadt */}
            <AddressFields
              wert={{ street, zip: zipcode, city }}
              onChange={(w) => { setStreet(w.street); setZip(w.zip); setCity(w.city) }}
            />
          </>
        )}

        {withProject && !isForty && (
          <>
            {/* Projekttyp | Gewerk | Mitarbeiter */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <F label="Projekttyp">
                <select value={projectTypeId} onChange={(e) => { setProjectTypeId(e.target.value); setStepId("") }}>
                  <option value="">–</option>
                  {options.data?.project_types?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </F>
              <F label="Gewerk">
                <select value={measureId} onChange={(e) => setMeasureId(e.target.value)}>
                  <option value="">-- kein Gewerk --</option>
                  {options.data?.measures?.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </F>
              <F label="Mitarbeiter">
                <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                  <option value="">-- kein Mitarbeiter --</option>
                  {options.data?.partners?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                  {options.data?.monteure
                    ?.filter((m) => !options.data?.partners?.some((p) => p.id === m.id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>
              </F>
            </div>

            {/* Projekt-Status (Steps des gewaehlten Typs) */}
            <F label="Projekt-Status" style={{ marginBottom: 12 }}>
              <select value={stepId} onChange={(e) => setStepId(e.target.value)}>
                <option value="">-- Standard --</option>
                {availableSteps.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </F>

            <F label={<>Projektname <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></>} style={{ marginBottom: 12 }}>
              <input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Standard: Name des Kontakts"
              />
            </F>

            {!projectOnly && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 12, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={separateProjectContact}
                  onChange={(e) => setSeparateProjectContact(e.target.checked)}
                />
                Abweichender Ansprechpartner
              </label>
            )}

            {/* Ansprechpartner-Felder (wenn Checkbox aktiv oder im projectOnly-Modus) */}
            {((!projectOnly && separateProjectContact) || projectOnly) && (
              <div style={{ marginBottom: 12, padding: 10, background: "var(--secondary-bg)", borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8 }}>
                  {projectOnly ? "Abweichender Ansprechpartner (optional)" : "Ansprechpartner"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <F label="Anrede">
                    <select value={cpSalutation} onChange={(e) => setCpSalutation(e.target.value)}>
                      <option value="Herr">Herr</option>
                      <option value="Frau">Frau</option>
                      <option value="Divers">Divers</option>
                    </select>
                  </F>
                  <div />
                  <F label="Vorname"><input value={cpFirst} onChange={(e) => setCpFirst(e.target.value)} /></F>
                  <F label="Nachname"><input value={cpLast} onChange={(e) => setCpLast(e.target.value)} /></F>
                  <F label="Telefon"><input value={cpPhone} onChange={(e) => setCpPhone(e.target.value)} /></F>
                  <F label="E-Mail"><input type="email" value={cpEmail} onChange={(e) => setCpEmail(e.target.value)} /></F>
                </div>
                <label
                  data-testid="cp-rechnungsempfaenger"
                  style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={cpRechnungsempfaenger}
                    onChange={(e) => setCpRechnungsempfaenger(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <strong>Rechnungsempfänger</strong>
                    <span style={{ display: "block", color: "var(--text-muted)", marginTop: 2, lineHeight: 1.45 }}>
                      Ohne Haken geht die Rechnung an den <strong>Kunden</strong>.
                      Mit Haken geht sie an die Anschrift des <strong>Ansprechpartners</strong> —
                      für den Fall, dass jemand anderes als der Kunde zahlt. Die <em>Anrede</em> im
                      Beleg bleibt in beiden Fällen der Kunde.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Projektadresse (abweichend vom Kontakt) */}
            {objektadressen.length > 0 && (
              <div style={{ marginBottom: 8, fontSize: 12.5 }}>
                <div style={{ color: "var(--text-muted)", marginBottom: 4 }}>
                  Weitere Objektadressen aus bisherigen Vorgängen:
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {objektadressen.map((a) => {
                    const gewaehlt = projectAddressOpen && projStreet === a.street && projZipcode === a.zipcode
                    return (
                      <button
                        key={`${a.street}|${a.zipcode}|${a.city}`}
                        type="button"
                        className="pp-btn pp-btn-sm"
                        title={a.vorgaenge.length ? a.vorgaenge.join("\n") : undefined}
                        style={{
                          fontSize: 12,
                          borderColor: gewaehlt ? "var(--accent-color)" : undefined,
                          background: gewaehlt ? "var(--accent-light)" : undefined,
                        }}
                        onClick={() => {
                          setProjectAddressOpen(true)
                          setProjStreet(a.street); setProjZipcode(a.zipcode); setProjCity(a.city)
                        }}
                      >
                        📍 {a.street}, {a.zipcode} {a.city}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={projectAddressOpen}
                onChange={(e) => setProjectAddressOpen(e.target.checked)}
              />
              Abweichende Projektadresse
            </label>
            {projectAddressOpen && (
              <div style={{ marginBottom: 12, padding: 10, background: "var(--secondary-bg)", borderRadius: 6 }}>
                <AddressFields
                  labelStrasse="Straße"
                  wert={{ street: projStreet, zip: projZipcode, city: projCity }}
                  onChange={(w) => { setProjStreet(w.street); setProjZipcode(w.zip); setProjCity(w.city) }}
                />
              </div>
            )}

            <F label="Projekt-Beschreibung" style={{ marginBottom: 16 }}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </F>
          </>
        )}

        {/* fortytools: Objekt (Facility) statt HERO-Projekt.
            Kein Gewerk/Pipeline/Kategorie — die kennt fortytools nicht.
            Der Einsatz selbst wird nicht per API gebucht (→ Übergabe-Aufgabe). */}
        {withProject && isForty && (
          <>
            <F label={<>Objekt-Name <span style={{ color: "#999", fontWeight: 400 }}>(z.B. Gebäude/Standort)</span></>} style={{ marginBottom: 12 }}>
              <input
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="Standard: Name des Kunden"
              />
            </F>
            <F label={<>Schlüsselbund <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></>} style={{ marginBottom: 12 }}>
              <input
                value={bunchofkeys}
                onChange={(e) => setBunchofkeys(e.target.value)}
                placeholder="z.B. Schlüssel-Nr. / Schließfach"
              />
            </F>
            <F label={<>Einsatzort-Notizen <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></>} style={{ marginBottom: 12 }}>
              <textarea
                value={facilityNotes}
                onChange={(e) => setFacilityNotes(e.target.value)}
                rows={3}
                placeholder="Hinweise zum Objekt (Zugang, Ansprechpartner vor Ort …)"
              />
            </F>

            {/* Objekt-Adresse (abweichend vom Kunden) — nutzt denselben project_address-Pfad */}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={projectAddressOpen}
                onChange={(e) => setProjectAddressOpen(e.target.checked)}
              />
              Abweichende Objekt-Adresse
            </label>
            {projectAddressOpen && (
              <div style={{ marginBottom: 12, padding: 10, background: "var(--secondary-bg)", borderRadius: 6 }}>
                <AddressFields
                  labelStrasse="Straße"
                  wert={{ street: projStreet, zip: projZipcode, city: projCity }}
                  onChange={(w) => { setProjStreet(w.street); setProjZipcode(w.zip); setProjCity(w.city) }}
                />
              </div>
            )}

            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
              Der Termin/Einsatz wird bei fortytools nicht automatisch angelegt — dafür entsteht eine Übergabe-Aufgabe am Objekt, das Büro plant den Einsatz in fortytools.
            </div>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none", border: "none",
              color: "var(--text-muted)", cursor: "pointer",
              fontSize: 13, fontFamily: "inherit",
            }}
          >
            Abbrechen
          </button>
          {(() => {
            const usingExisting = mode === "calendar" && useExisting
            // 🔴 Ein reiner Firmenkontakt hat KEINEN Nachnamen. Die erste
            // Fassung von #49 lockerte nur das `required` am Feld — der
            // Knopf blieb gesperrt, und der Klick tat sichtbar gar nichts.
            // Beim Durchklicken auf dev am 27.08.2026 aufgefallen; weder die
            // Suite noch der Build noch der API-Test sahen es.
            const hatNamen = !!last.trim() || !!firma.trim()
            const submitDisabled = create.isPending
              || (usingExisting ? !existingContact : (!projectOnly && !hatNamen))
            return (
              <button
                type="submit"
                disabled={submitDisabled}
                className={cn("pp-btn pp-btn-primary", submitDisabled && "opacity-50 cursor-not-allowed")}
                style={{ fontSize: 13 }}
              >
                {create.isPending ? "Erstellt..." : title}
              </button>
            )
          })()}
        </div>
      </form>
    </div>,
    document.body,
  )
}

function F({
  label, children, style,
}: {
  label: React.ReactNode
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <div style={style}>
      <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--text-secondary)" }}>
        {label}
      </label>
      {children}
    </div>
  )
}
